import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../locales/i18n";

const API_URL = "https://dev.api.jomuntu.com/api/v1/users";
const API_URL_RESTAURANT = "https://dev.api.jomuntu.com/api/v1/food-venues/sign-up";

interface Role {
  id: number;
  name: string;
}

interface User {
  id: number;
  roles: Role[];
  name: string;
  email: string;
  phoneNumber: string;
  adminName: string;
  ownerName: string;
  ownerEmail: string;
  openingHours: string;
  category: string;
  location: string;
}

interface AllUsersState {
  users: User[];
  loading: boolean;
  error: string | null;
  currentRole: string;
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

const initialState: AllUsersState = {
  users: [],
  loading: false,
  error: null,
  currentRole: "USER",
  page: 0,
  size: 4,
  totalPages: 0,
  totalElements: 0,
};

export const fetchUsers = createAsyncThunk(
  "allUsers/fetchUsers",
  async (
    {
      role,
      page,
      size,
      search,
    }: { role: string; page: number; size: number; search?: string },
    { rejectWithValue }
  ) => {
    try {
      const token = localStorage.getItem("token");

      const params: any = { role, page, size };
      if (search) params.search = search;

      const response = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(i18n.t("error.fetchUsers"));
    }
  }
);

export const deleteUser = createAsyncThunk(
  "allUsers/deleteUser",
  async (id: number, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return id;
    } catch (error) {
      return rejectWithValue(i18n.t("error.deleteUser"));
    }
  }
);

const allUsersSlice = createSlice({
  name: "allUsers",
  initialState,
  reducers: {
    setRole: (state, action) => {
      state.currentRole = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.content;
        state.totalPages = action.payload.totalPages;
        state.totalElements = action.payload.totalElements;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users = state.users.filter((user) => user.id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.error = action.payload as string;
      })
  },
});

export const { setRole, setPage } = allUsersSlice.actions;

export default allUsersSlice.reducer;
